<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />

        <div class="flex flex-1 flex-col lg:pl-64">
            <main class="flex-1">
                <div class="mx-auto w-full max-w-7xl px-2 pt-12 sm:px-4 lg:pt-4">
                    <div class="py-2">
                        <Breadcrumbs :links="breadcrumbsLinks" />
                    </div>

                    <div class="space-y-6 pt-6 pb-24 md:pb-6">
                        <div class="www px-2">
                            <h3 class="py-2 text-lg font-medium select-none">
                                {{ t('placeholderBasic') }}
                            </h3>
                        </div>

                        <div class="sm:grid sm:grid-cols-2 sm:gap-4">
                            <div class="www px-2 py-2">
                                <label
                                    for="name"
                                    class="block text-sm font-medium text-gray-700 select-none"
                                >
                                    {{ t('placeholderName') }}
                                </label>

                                <div class="mt-1 flex">
                                    <input
                                        id="name"
                                        v-model="placeholder.name"
                                        type="text"
                                        name="name"
                                        autocomplete="off"
                                        class="block w-full grow rounded-l-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                        @change="codeize()"
                                        @input="codeize()"
                                    />
                                    <select
                                        id="placeholders"
                                        v-model="placeholder.type"
                                        name="placeholders"
                                        :class="[
                                            isImageType(placeholder)
                                                ? 'rounded-none'
                                                : 'rounded-r-md',
                                            'border border-l-0 border-gray-300 bg-white font-medium shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm',
                                        ]"
                                        :disabled="placeholderDefinitionId !== '0'"
                                        @change="placeholderTypeCheck()"
                                    >
                                        <option
                                            v-for="placeholderType in placeholderTypes"
                                            :key="placeholderType.value"
                                            class="font-medium"
                                            :value="placeholderType.value"
                                            :disabled="!placeholderType.enabled"
                                        >
                                            {{ placeholderType.name }}
                                        </option>
                                    </select>
                                    <ActionButton
                                        v-if="
                                            isImageType(placeholder) &&
                                            placeholderDefinitionId === '0'
                                        "
                                        :value="t('imageCodeChangeButton')"
                                        state="info"
                                        :icon="MdiRefresh"
                                        class-override="rounded-l-none "
                                        @click="placeholderTypeCheck()"
                                    />
                                </div>
                            </div>
                            <div class="www px-2 py-2">
                                <label
                                    for="description"
                                    class="block text-sm font-medium text-gray-700 select-none"
                                >
                                    {{ t('placeholderDescription') }}
                                </label>
                                <input
                                    id="description"
                                    v-model="placeholder.description"
                                    type="text"
                                    name="description"
                                    autocomplete="off"
                                    class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                />
                            </div>
                            <div class="www flex justify-between px-2 py-2">
                                <h3 class="text-lg font-medium select-none">
                                    {{ t('placeholderKeys') }}
                                </h3>
                                <ActionButton
                                    :value="t('buttonAdd')"
                                    state="primary"
                                    :icon="MdiPlus"
                                    :is-disabled="placeholder.type === 'IMAGECODE'"
                                    @click="placeholderKeyDialogOpen()"
                                />
                            </div>
                            <div class="www flex justify-between px-2 py-2">
                                <h3 class="text-lg font-medium select-none">
                                    {{ t('placeholderKeySampleValues') }}
                                </h3>
                                <ActionButton
                                    :value="t('buttonAddRow')"
                                    state="primary"
                                    :icon="MdiPlus"
                                    :is-disabled="
                                        Object.keys(placeholder.model).length === 0 ||
                                        placeholder.type === 'IMAGECODE'
                                    "
                                    @click="addRow()"
                                />
                            </div>
                        </div>
                        <!-- Model keys -->
                        <template
                            v-for="(item, index) in placeholder.model"
                            :key="`${item}`"
                        >
                            <div
                                class="rounded-md border border-gray-300 hover:border-blue-300 sm:grid sm:grid-cols-2 sm:gap-4"
                            >
                                <div
                                    class="col-span-2 flex items-center justify-between space-x-2 rounded-t-md border-b border-gray-300 bg-gray-50"
                                >
                                    <label
                                        :for="`uiconfig-${item}-${index}`"
                                        class="block px-4 py-2 text-sm font-medium text-gray-700 select-none"
                                    >
                                        <strong>{{ placeholder.uiConfig[item] || item }}</strong>
                                    </label>
                                    <button
                                        v-tippy="t('buttonRemove')"
                                        type="button"
                                        class="inline-flex items-center space-x-2 rounded-tr-md border-0 px-4 py-2 text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-hidden"
                                        :class="
                                            placeholderKeyRemoveButtonDisabled(placeholder) ||
                                            placeholder.model.length === 1
                                                ? 'cursor-not-allowed border-gray-300 bg-white text-gray-300'
                                                : 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                        "
                                        :disabled="
                                            placeholderKeyRemoveButtonDisabled(placeholder) ||
                                            placeholder.model.length === 1
                                        "
                                        @click="removeKey(index)"
                                    >
                                        <MdiClose class="h-5 w-5" />
                                    </button>
                                </div>
                                <div class="l">
                                    <div class="www px-4 py-2">
                                        <div
                                            class="block text-sm font-medium text-gray-700 select-none"
                                        >
                                            {{ t('placeholderKey') }}
                                        </div>

                                        <div
                                            class="flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                        >
                                            <div>{{ item }}</div>
                                            <div class="flex-none"></div>
                                        </div>
                                    </div>
                                    <div class="www px-4 py-2">
                                        <label
                                            :for="`uiconfig-${item}-${index}`"
                                            class="block text-sm font-medium text-gray-700 select-none"
                                        >
                                            {{ t('placeholderKeyDescription') }}
                                        </label>
                                        <div class="flex rounded-md shadow-sm">
                                            <input
                                                :id="`uiconfig-${item}-${index}`"
                                                v-model="placeholder.uiConfig[item]"
                                                :placeholder="item"
                                                type="text"
                                                :name="`uiconfig-${item}-${index}`"
                                                autocomplete="off"
                                                :readonly="isImageType(placeholder)"
                                                class="block w-full rounded-md border border-gray-300 px-3 py-2 text-xs shadow-sm ring-0 focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div class="r">
                                    <div
                                        v-for="(value, key) in placeholder.sample"
                                        :key="`${key}`"
                                    >
                                        <div class="www px-4 py-2">
                                            <label
                                                :for="`sample-key-${item}-${index}-${key}`"
                                                class="block text-sm font-medium text-gray-700 select-none"
                                            >
                                                {{ key }}. {{ t('placeholderKeyValue') }} pro
                                                {{ item }}
                                            </label>
                                            <div class="flex rounded-md shadow-sm">
                                                <input
                                                    :id="`sample-key-${item}-${index}-${key}`"
                                                    v-model="placeholder.sample[key][item]"
                                                    :placeholder="item"
                                                    type="text"
                                                    :name="`sample-key-${item}-${index}-${key}`"
                                                    autocomplete="off"
                                                    class="block w-full rounded-l-md border border-gray-300 px-3 py-2 text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                                />
                                                <button
                                                    v-tippy="t('buttonRemove')"
                                                    type="button"
                                                    class="inline-flex items-center space-x-2 rounded-r-md border px-4 py-2 text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-hidden"
                                                    :class="
                                                        placeholderKeyRemoveButtonDisabled(
                                                            placeholder,
                                                        ) || key === 0
                                                            ? 'cursor-not-allowed border-l-0 border-gray-300 bg-gray-50 text-gray-300'
                                                            : 'cursor-pointer border-l-0 border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                    "
                                                    :disabled="
                                                        placeholderKeyRemoveButtonDisabled(
                                                            placeholder,
                                                        ) || key === 0
                                                    "
                                                    @click="removeRow(key)"
                                                >
                                                    <MdiClose class="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div
                        class="www fixed right-0 bottom-0 left-0 z-10 flex items-center justify-center gap-x-2 bg-white px-2 py-2 shadow-[0px_20px_30px_4px_rgba(0,0,0,.6)] md:static md:justify-end md:bg-transparent md:shadow-none"
                    >
                        <input
                            id="dropzone-file"
                            ref="dropzone"
                            type="file"
                            class="hidden"
                            accept="application/json"
                            @change="uploadPlaceholderData()"
                        />
                        <ActionButton
                            :value="t('buttonBack')"
                            state="neutral"
                            :icon="MdiChevronLeft"
                            @click="router.push(backRoute)"
                        />
                        <ActionButton
                            v-if="canImport(placeholderDefinitionId)"
                            :value="t('buttonImport')"
                            state="warning"
                            :icon="MdiDatabaseImport"
                            @click="dropzone.click()"
                        />
                        <ActionButton
                            v-if="!canImport(placeholderDefinitionId)"
                            :value="t('buttonExport')"
                            state="info"
                            :icon="MdiDatabaseExport"
                            @click="downloadPlaceholderData()"
                        />
                        <ActionButton
                            :value="t('buttonSave')"
                            state="primary"
                            :icon="MdiContentSave"
                            :is-disabled="
                                saving.active === true ||
                                placeholder.model.length === 0 ||
                                placeholder.name.trim() === ''
                            "
                            @click="savePlaceholderData(placeholder)"
                        />
                    </div>
                </div>
            </main>
        </div>
    </div>
    <PlaceholderKeyDialog
        :key="placeholderKeyDialogKey"
        :placeholder="placeholder"
        :state="placeholderKeyDialogState"
    />

    <ImageCodeWizzard
        :key="wizzardDialogKey"
        :state="wizzardDialogState"
        :placeholder="placeholder"
    />

    <Spinner
        v-if="saving.active"
        type="complex"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="saving.text"
        :inline="false"
        :pulse="true"
    />
</template>
<script setup>
import { ref, reactive /* watch */ } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Headify from '../components/Headify.vue';
import Spinner from '../ui/Spinner.vue';
import ImageCodeWizzard from '../components/ImageCodeWizzard.vue';
import Breadcrumbs from '../ui/Breadcrumbs.vue';
import NavMain from '../ui/NavMain.vue';
import { notify } from 'notiwind';
import PlaceholderKeyDialog from '../components/PlaceholderKeyDialog.vue';
import ActionButton from '../ui/ActionButton.vue';
import navs from '../composables/navs';
import { getPlaceholderTypes, isImageType } from '../composables/usePlaceholderTypes';
import { savePlaceholder, getPlaceholder } from '../composables/usePlaceholders';
import { exportPlaceholderGroup, importPlaceholderGroup } from '../composables/useImportExport';
import {
    stringToCode,
    toLatin128,
    getRndKey,
    cloneData,
    resolveBackUrl,
} from '../composables/utils';
//import
import { t } from '../composables/i18n';

import {
    MdiClose,
    MdiContentSave,
    MdiChevronLeft,
    MdiPlus,
    MdiDatabaseImport,
    MdiDatabaseExport,
    MdiRefresh,
} from 'materialdesignicons-vue3/icons/';

// States
const saving = reactive({
    active: false,
    text: `${t('saving')}`,
});
const route = useRoute();
const router = useRouter();
const placeholderDefinitionId = route.params.placeholderid;
const placeholderKeyDialogKey = ref(getRndKey());
const placeholderKeyDialogState = reactive({
    open: false,
    title: `${t('placeholderNewKey')}`,
    overlayPreventClose: true,
});
const wizzardDialogKey = ref(getRndKey());
const wizzardDialogState = reactive({
    open: false,
    overlayPreventClose: true,
});

const placeholderTypes = getPlaceholderTypes();

let placeholder = reactive(
    (await getPlaceholder(placeholderDefinitionId)) || {
        name: '',
        description: '',
        uiConfig: '{}',
        model: [],
        sample: [],
        type: 'SIMPLE',
    },
);
const backRoute = resolveBackUrl(router, route, 'placeholders');
const breadcrumbsLinks = [
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        ...backRoute,
        title: t('Placeholders'),
    },
    {
        title: `${t('Placeholder')}`,
        final: true,
    },
];

const dropzone = ref(null);

const uploadPlaceholderData = async (fileInputId = '#dropzone-file') => {
    const input = document.querySelector(fileInputId).files[0];
    const readFile = new FileReader();
    readFile.onload = (e) => {
        const contents = e.target.result;
        try {
            const model = JSON.parse(contents);
            placeholder = importPlaceholderGroup(model);
            savePlaceholderData();
        } catch (err) {
            // alert here
            console.error('Error parsing placeholder file', err);
            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: `Parse error ${input.name}`,
                },
                5000,
            );
        }
    };
    readFile.readAsText(input);
};

const downloadPlaceholderData = () => {
    const exportPlacehoder = exportPlaceholderGroup(cloneData(placeholder));
    const blob = new Blob([JSON.stringify(exportPlacehoder, null, 4)], {
        type: 'application/json',
    });
    const linkSource = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = linkSource;
    downloadLink.download = `TEMPLATING-EXPORT-PLACEHOLDER-${exportPlacehoder.name}.json`;
    downloadLink.click();
};

const savePlaceholderData = async () => {
    saving.active = true;
    //console.log('savePlaceholderData placeholder', placeholder);
    if (typeof placeholder.uiConfig !== 'object' || placeholder.uiConfig === null) {
        placeholder.uiConfig = {};
    }
    placeholder.uiConfig = JSON.stringify(placeholder.uiConfig);
    const placeholderToSave = cloneData(placeholder);
    placeholderToSave.type === 'IMAGECODE'
        ? (placeholderToSave.type = 'SIMPLE')
        : placeholderToSave.type;
    const res = await savePlaceholder(placeholderDefinitionId, placeholderToSave, saving);

    if (res !== false) {
        saving.active = false;
        notify(
            {
                group: 'success',
                title: 'Done',
                text: `Placeholder "${placeholder.name}" was successfully saved`,
            },
            2500,
        );
        router.push(backRoute);
    } else {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: `Error saving placeholder ${placeholder.name}`,
            },
            5000,
        );
        saving.active = false;
    }
};

const transformUiConfig = (placeholder) => {
    let uiConfigOrigin = JSON.parse(placeholder.uiConfig);

    if (typeof uiConfigOrigin !== 'object' || uiConfigOrigin === null) {
        uiConfigOrigin = {};
    }
    const transformed = {};
    placeholder.model.map((item) => {
        transformed[item] = uiConfigOrigin[item] || '';
    });
    return (placeholder.uiConfig = transformed);
};

const placeholderKeyDialogOpen = () => {
    placeholderKeyDialogState.open = true;
    placeholderKeyDialogKey.value = getRndKey();
};

const wizzardDialogOpen = () => {
    wizzardDialogState.open = true;
    wizzardDialogKey.value = getRndKey();
};

const placeholderTypeCheck = () => {
    if (isImageType(placeholder)) {
        wizzardDialogOpen();
        //console.log('placeholderTypeCheck placeholder.type', placeholder.type);
    }
};

const placeholderKeyRemoveButtonDisabled = (placeholder) => {
    return placeholder.type === 'IMAGECODE';
};

const canImport = (placeholderDefinitionId) => {
    return placeholderDefinitionId === '0';
};

const removeKey = (index) => {
    const key = placeholder.model[index];
    placeholder.model.splice(index, 1);
    for (let i in placeholder.sample) {
        delete placeholder.sample[i][key];
    }

    if (placeholder.uiConfig) {
        placeholder.uiConfig[key] = null;
        delete placeholder.uiConfig[key];
    }
};

const addRow = () => {
    const newRow = {};
    placeholder.model.forEach((item) => {
        newRow[item] = '';
    });
    placeholder.sample.push(newRow);
};

const removeRow = (key) => {
    for (let i in placeholder.sample) {
        if (i == key) {
            placeholder.sample.splice(i, 1);
        }
    }
};

const codeize = () => {
    placeholder.name = toLatin128(stringToCode(placeholder.name));
};

transformUiConfig(placeholder);
//console.log('INIT placeholder', placeholder);
const headMeta = navs().metaFind(route.name);

/* watch(placeholder, () => {
    if (isImageType(placeholder)) {
        transformUiConfig(placeholder);
        //console.log('WATCH placeholder.type', placeholder.type);
    }
}); */
</script>
